import {analyticsTracker} from "./analytics.tracker";

const ITEM_SELECTOR = '[data-ga-item]';
const LIST_SELECTOR = '[data-ga-list-id]';
const VIEW_ITEM_SELECTOR = '[data-ga-view-item]';
const SEARCH_SELECTOR = '[data-ga-search-term]';
const DOWNLOAD_SELECTOR = '[data-ga-download]';
const LOGIN_SELECTOR = '[data-ga-login]';
const LEAD_EVENT_SELECTOR = '[data-ga-lead-event]';

const INDEX_ATTRIBUTE = 'analyticsIndex';

// Swiper's loop mode clones slides into the list container. The clones carry the
// same data-ga-item payload, so counting them would multiply every impression.
const CLONE_SELECTOR = '.swiper-slide-duplicate';

const parseJson = function (value) {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch (e) {
        return null;
    }
};

const fileNameOf = function (element) {
    if (element.dataset.gaFileName) {
        return element.dataset.gaFileName;
    }

    // A link to a folder ends in a slash, so its last segment is empty and the one
    // before it is the name that means anything in a report.
    const segments = element.pathname.split('/').filter(Boolean);

    return decodeURIComponent(segments.pop() || '');
};

const extensionOf = function (fileName) {
    const parts = fileName.split('.');

    return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const withoutEmpty = function (params) {
    return Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    );
};

// Which field carries the subject is the form's business, not this file's -- the blade
// names it. The label, not the stored value, is what means anything in a GA4 report: the
// options are config keys ("1".."5"), and the labels read the same in every language.
const subjectOf = function (form) {
    const field = form.dataset.gaSubjectField;

    if (!field) {
        return '';
    }

    const select = form.querySelector(`select[name="${field}"]`);

    if (!select?.value) {
        return '';
    }

    return select.options[select.selectedIndex]?.text.trim() || '';
};

const itemElementsIn = function (list) {
    return Array.from(list.querySelectorAll(ITEM_SELECTOR))
        .filter((element) => !element.closest(CLONE_SELECTOR));
};

class AnalyticsService {
    constructor(analytics) {
        this.analytics = analytics;
        this.emitted = {};
        // Keyed by the list element, not by its data-ga-list-id: a page can hold several
        // blocks that legitimately report under the same item_list_id, and keying on the
        // id would let them overwrite each other's signature and re-emit on every refresh.
        this.emittedLists = new WeakMap();
    }

    // Once per page load.
    start() {
        this.#trackLogin();
        this.#trackFlashedLead();
        this.#trackViewItem();
        this.#listenForClicks();
        this.refresh();
    }

    // After any content swap: the trackers that read whatever is currently rendered.
    refresh() {
        this.#trackSearch();
        this.#trackProductLists();
    }

    trackFormLead(form) {
        const leadType = form?.dataset.gaLeadType;

        if (!leadType) {
            return;
        }

        this.analytics.trackGenerateLead(leadType, withoutEmpty({
            form_name: form.dataset.gaFormName,
            form_subject: subjectOf(form),
        }));
    }

    // Reports X only when its signature differs from the last one reported for X.
    #isNew(key, signature) {
        if (this.emitted[key] === signature) {
            return false;
        }

        this.emitted[key] = signature;

        return true;
    }

    // The blade only emits the marker for terms worth reporting, so its presence is the signal.
    #trackSearch() {
        const element = document.querySelector(SEARCH_SELECTOR);

        if (!element) {
            // A query too short to report clears the last term, so searching for it again
            // after one of those is still a new search rather than a repeat.
            delete this.emitted[SEARCH_SELECTOR];

            return;
        }

        const term = element.dataset.gaSearchTerm;

        if (!this.#isNew(SEARCH_SELECTOR, term)) {
            return;
        }

        this.analytics.trackSearch(term, Number(element.dataset.gaSearchResults));
    }

    // The flash that renders this marker lives for one request, so the event cannot repeat.
    #trackLogin() {
        const method = document.querySelector(LOGIN_SELECTOR)?.dataset.gaLogin;

        if (!method) {
            return;
        }

        this.analytics.trackLogin(method);
    }

    #trackFlashedLead() {
        const lead = parseJson(document.querySelector(LEAD_EVENT_SELECTOR)?.dataset.gaLeadEvent);

        if (!lead?.lead_type) {
            return;
        }

        const {lead_type: leadType, ...params} = lead;

        this.analytics.trackGenerateLead(leadType, params);
    }

    #trackViewItem() {
        const element = document.querySelector(VIEW_ITEM_SELECTOR);
        const item = parseJson(element?.dataset.gaViewItem);

        if (!item) {
            return;
        }

        this.analytics.trackViewItem(item);
    }

    // Signatures are built from the raw payloads so an unchanged list costs no JSON parsing.
    #trackProductLists() {
        document.querySelectorAll(LIST_SELECTOR).forEach((list) => {
            const elements = itemElementsIn(list);
            const signature = elements.map((element) => element.dataset.gaItem).join(',');

            if (this.emittedLists.get(list) === signature) {
                return;
            }

            const items = this.#itemsIn(elements);

            if (!items.length) {
                return;
            }

            this.emittedLists.set(list, signature);

            this.analytics.trackViewItemList(list.dataset.gaListId, list.dataset.gaListName, items);
        });
    }

    // One delegated listener. A download link rendered inside a product tile is both
    // a selection and a download, and both are meant to report -- handling them here
    // states that, instead of leaving it to the order two listeners were registered in.
    #listenForClicks() {
        document.addEventListener('click', (event) => {
            this.#reportSelectItem(event.target);
            this.#reportFileDownload(event.target);
        });
    }

    #reportSelectItem(target) {
        if (!target.closest('a')) {
            return;
        }

        const element = target.closest(ITEM_SELECTOR);
        const item = parseJson(element?.dataset.gaItem);

        if (!item) {
            return;
        }

        const list = element.closest(LIST_SELECTOR);

        this.analytics.trackSelectItem(list?.dataset.gaListId, list?.dataset.gaListName, {
            ...item,
            // Swiper clones copy the stamp of the slide they were cloned from.
            index: Number(element.dataset[INDEX_ATTRIBUTE]) || 0,
        });
    }

    #reportFileDownload(target) {
        const element = target.closest(DOWNLOAD_SELECTOR);

        if (!element) {
            return;
        }

        const fileName = fileNameOf(element);

        this.analytics.trackFileDownload(withoutEmpty({
            document_type: element.dataset.gaDownload,
            file_name: fileName,
            file_extension: extensionOf(fileName),
            link_url: element.href,
            link_text: element.textContent.trim(),
            ...this.#productContext(element),
        }));
    }

    // A download belongs to a product only when it is rendered inside that product's
    // marker. Resolving by ancestry rather than page-wide is what stops a CMS block
    // from inheriting context that is not its own.
    #productContext(element) {
        return parseJson(element.closest(VIEW_ITEM_SELECTOR)?.dataset.gaViewItem) || {};
    }

    #itemsIn(elements) {
        return elements
            .map((element, index) => {
                element.dataset[INDEX_ATTRIBUTE] = index;

                const item = parseJson(element.dataset.gaItem);

                return item ? {...item, index: index} : null;
            })
            .filter(Boolean);
    }
}

const analyticsService = new AnalyticsService(analyticsTracker);
export default analyticsService;
